import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { authApi } from '../api/auth';

type Step = 'email' | 'code' | 'password' | 'done';

const COOLDOWN_SECONDS = 30;
const RESEND_LOCK_SECONDS = 300;

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const queryEmail = searchParams.get('email') || '';
  const queryCode = searchParams.get('code') || '';
  const isExpired = searchParams.get('expired') === 'true';
  const isInvalid = searchParams.get('invalid') === 'true';

  const [step, setStep] = useState<Step>(queryEmail && queryCode ? 'code' : 'email');
  const [email, setEmail] = useState(queryEmail);
  const [code, setCode] = useState(queryCode);
  const [password, setPassword] = useState('');

  const [resendCount, setResendCount] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState<number>(0);
  const [lockUntil, setLockUntil] = useState<number>(0);

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isExpired) {
      setError('인증 코드가 만료되었습니다. 코드를 다시 요청해 주세요.');
      setStep('email');
    }
    if (isInvalid) {
      setError('인증 코드가 올바르지 않습니다. 코드를 다시 확인해 주세요.');
      setStep('code');
    }
  }, [isExpired, isInvalid]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now();
      if (cooldownUntil && now >= cooldownUntil) setCooldownUntil(0);
      if (lockUntil && now >= lockUntil) {
        setLockUntil(0);
        setResendCount(0);
      }
    }, 1000);

    return () => window.clearInterval(timer);
  }, [cooldownUntil, lockUntil]);

  const cooldownLeft = useMemo(() => {
    if (!cooldownUntil) return 0;
    return Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
  }, [cooldownUntil]);

  const lockLeft = useMemo(() => {
    if (!lockUntil) return 0;
    return Math.max(0, Math.ceil((lockUntil - Date.now()) / 1000));
  }, [lockUntil]);

  const requestCode = async () => {
    if (lockUntil && Date.now() < lockUntil) {
      setError(`재요청 제한 중입니다. ${lockLeft}초 후 다시 시도해 주세요.`);
      return;
    }
    if (cooldownUntil && Date.now() < cooldownUntil) {
      setError(`코드 재발송은 ${cooldownLeft}초 후 가능합니다.`);
      return;
    }

    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await authApi.requestPasswordReset({ email });
      const { isSuccess, result, message: apiMessage } = response.data;
      if (!isSuccess) {
        setError(apiMessage || '코드 발송에 실패했습니다.');
        return;
      }

      const nextCount = resendCount + 1;
      setResendCount(nextCount);
      setCooldownUntil(Date.now() + COOLDOWN_SECONDS * 1000);
      setStep('code');

      const expiresIn = result?.expiresInSeconds ?? 300;
      setMessage(`인증 코드가 발송되었습니다. 코드 유효시간: ${expiresIn}초`);

      if (nextCount >= 3) {
        setLockUntil(Date.now() + RESEND_LOCK_SECONDS * 1000);
        setMessage('코드 재요청이 3회를 초과하여 5분 동안 제한됩니다.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '요청 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    await requestCode();
  };

  const handleValidateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await authApi.validatePasswordResetCode({ email, code });
      const { isSuccess, result, message: apiMessage } = response.data;
      if (!isSuccess || !result) {
        setError(apiMessage || '코드 검증에 실패했습니다.');
        return;
      }

      if (result.expired) {
        setError('코드가 만료되었습니다. 다시 요청해 주세요.');
        setStep('email');
        return;
      }

      if (result.revoked) {
        setError('이미 사용되었거나 폐기된 코드입니다. 다시 요청해 주세요.');
        setStep('email');
        return;
      }

      if (!result.valid) {
        setError('코드가 유효하지 않습니다. 다시 입력해 주세요.');
        return;
      }

      setStep('password');
      setMessage('코드가 확인되었습니다. 새 비밀번호를 입력해 주세요.');
    } catch (err: any) {
      setError(err.response?.data?.message || '코드 검증 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await authApi.confirmPasswordReset({ email, code, password });
      const { isSuccess, message: apiMessage } = response.data;
      if (!isSuccess) {
        setError(apiMessage || '비밀번호 변경에 실패했습니다.');
        return;
      }
      setStep('done');
      setMessage('비밀번호가 변경되었습니다. 로그인 페이지로 이동합니다.');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err: any) {
      setError(err.response?.data?.message || '비밀번호 변경 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const canResend = !loading && !lockUntil && !cooldownUntil;

  return (
    <div className="auth-page">
      <div className="auth-orbit auth-orbit-a" aria-hidden="true" />
      <div className="auth-orbit auth-orbit-b" aria-hidden="true" />
      <div className="auth-orbit auth-orbit-c" aria-hidden="true" />

      <div className="auth-shell auth-shell-login-only fade-in">
        <div className="auth-brand-corner" aria-hidden="true">
          <div className="auth-brand-wordmark">omongkr</div>
        </div>

        <section className="auth-panel card">
          <div className="auth-panel-head">
            <h2 className="auth-panel-title">비밀번호 재설정</h2>
            <p className="auth-panel-copy">이메일 확인 후 새 비밀번호를 설정합니다.</p>
          </div>

          {step === 'email' && (
            <form onSubmit={handleRequestReset} className="auth-form">
              <div className="auth-field">
                <label className="label">이메일</label>
                <input
                  type="email"
                  className="input auth-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="name@company.com"
                />
              </div>
              <button className="btn btn-primary auth-submit-btn" type="submit" disabled={loading}>
                {loading ? '요청 중...' : <>코드 발송 <ArrowRight size={18} /></>}
              </button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={handleValidateCode} className="auth-form">
              <div className="auth-field">
                <label className="label">이메일</label>
                <input
                  type="email"
                  className="input auth-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="name@company.com"
                />
              </div>

              <div className="auth-field">
                <label className="label">인증 코드</label>
                <input
                  className="input auth-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="6자리 코드"
                  required
                />
              </div>

              <button className="btn btn-primary auth-submit-btn" type="submit" disabled={loading}>
                {loading ? '검증 중...' : <>코드 확인 <ArrowRight size={18} /></>}
              </button>

              <button type="button" className="btn btn-outline auth-submit-btn" disabled={!canResend} onClick={requestCode}>
                {lockUntil
                  ? `재발송 제한 ${lockLeft}s`
                  : cooldownUntil
                    ? `재발송 ${cooldownLeft}s`
                    : '코드 재발송'}
              </button>
            </form>
          )}

          {step === 'password' && (
            <form onSubmit={handleConfirmReset} className="auth-form">
              <div className="auth-field">
                <label className="label">새 비밀번호</label>
                <input
                  type="password"
                  className="input auth-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                />
              </div>

              <button className="btn btn-primary auth-submit-btn" type="submit" disabled={loading}>
                {loading ? '변경 중...' : <>비밀번호 변경 <ArrowRight size={18} /></>}
              </button>
            </form>
          )}

          {step === 'done' && (
            <div className="sso-message">비밀번호 변경이 완료되었습니다. 로그인으로 이동합니다.</div>
          )}

          {error && <div className="auth-error-box" style={{ marginTop: 16 }}>{error}</div>}
          {message && <div className="sso-message" style={{ marginTop: 16 }}>{message}</div>}

          <div className="auth-inline-links" style={{ marginTop: '1.25rem' }}>
            <span>로그인으로 돌아가기</span>
            <button type="button" className="auth-link-btn" onClick={() => navigate('/login')}>
              로그인
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
